import { useRef, useState, useEffect } from "react";
import styles from "./OrderOutfitDetail.module.scss";
import { useParams } from "react-router-dom";
import { api } from "../../../api/api";
import apiMethods from "../../../api/apiMethods";
import { Table } from "@/js/modules/Table/Table";
import { Link } from "react-router-dom";

const ImageMagnifier = ({ src, alt, zoomFactor = 2 }) => {
	const [showMagnifier, setShowMagnifier] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
	const imgRef = useRef(null);

	const handleMouseEnter = () => {
		if (imgRef.current) {
			setImgSize({
				width: imgRef.current.clientWidth,
				height: imgRef.current.clientHeight,
			});
		}
		setShowMagnifier(true);
	};

	const handleMouseMove = e => {
		const rect = e.currentTarget.getBoundingClientRect();
		const x = ((e.clientX - rect.left) / imgSize.width) * 100;
		const y = ((e.clientY - rect.top) / imgSize.height) * 100;
		setPosition({ x, y });
	};

	return (
		<div
			className={styles.magnifierContainer}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={() => setShowMagnifier(false)}
			onMouseMove={handleMouseMove}
		>
			<img ref={imgRef} src={src} alt={alt} className={styles.magnifierImage} />
			{showMagnifier && (
				<div
					className={styles.magnifierLens}
					style={{
						left: `${position.x}%`,
						top: `${position.y}%`,
						backgroundImage: `url(${src})`,
						backgroundPosition: `${position.x}% ${position.y}%`,
						backgroundSize: `${imgSize.width * zoomFactor}px ${imgSize.height * zoomFactor}px`,
					}}
				/>
			)}
		</div>
	);
};

export function OrderOutfitDetail({ initialData, onStatusChange }) {
	const [loading, setLoading] = useState(false);
	const [orderData, setOrderData] = useState(null);
	const [positions, setPositions] = useState([]);
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [showPopup, setShowPopup] = useState(null);
	const [isTableVisible, setIsTableVisible] = useState(false);
	const toggleTable = () => {
		setIsTableVisible(!isTableVisible);
	};
	const [rejectData, setRejectData] = useState({
		reason: "invalidData",
		reasonText: "",
	});
	const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
	const rejectButtonRef = useRef(null);
	const [selectedPositionId, setSelectedPositionId] = useState(null);

	// Добавить в начало компонента
	const singlePopupRef = useRef(null);
	const [singlePopupPosition, setSinglePopupPosition] = useState({
		top: 0,
		left: 0,
	});

	const { slug } = useParams();

	// Функция загрузки данных с сервера
	const fetchOrderData = async () => {
		setLoading(true);
		try {
			const response = await api.post(`/distributor/service_orders/${slug}`);
			const order = response.data.data;
			console.log("order", order);
			transformOrderData(order);
		} catch (error) {
			console.error("Error fetching order data:", error);
		} finally {
			setLoading(false);
		}
	};

	// Функция обновления основного статуса заказа на основе позиций
	const updateMainOrderStatus = (order, updatedPositions) => {
		// Подсчитываем статусы позиций
		const hasRejected = updatedPositions.some(p => p.status === "rejected");
		const allConfirmed = updatedPositions.every(p => p.status === "confirmed");
		const hasConfirmed = updatedPositions.some(p => p.status === "confirmed");
		const hasPending = updatedPositions.some(p => p.status === "pending");

		let newSlug = order.status?.slug || "new";
		let newI18n = order.status?.i18n || "Новый";

		if (allConfirmed) {
			newSlug = "approved";
			newI18n = "Подтверждено";
		} else if (hasRejected && !hasPending) {
			newSlug = "rejected";
			newI18n = "Отклонено";
		} else if (hasConfirmed && hasPending) {
			newSlug = "partially_approved";
			newI18n = "Частично подтверждено";
		} else if (hasRejected && hasPending) {
			newSlug = "partially_approved";
			newI18n = "Частично подтверждено";
		}

		// Обновляем статус заказа
		setOrderData(prev => ({
			...prev,
			status: {
				...prev?.status,
				slug: newSlug,
				i18n: newI18n,
			},
		}));
	};

	const transformOrderData = order => {
		setOrderData(order);

		// Трансформируем goods в нужный формат для таблицы
		const transformedPositions = order.goods.map(good => ({
			id: good.id,
			oil: good.oil_name,
			container:
				good.container_type === "canister"
					? "Канистра"
					: good.container_type === "pouring"
						? "Разлив"
						: good.container_type,
			liters: parseFloat(good.liters),
			quantity: parseFloat(good.quantity),
			price: parseFloat(good.sum),
			status:
				good.status?.slug === "new"
					? "pending"
					: good.status?.slug === "approved"
						? "confirmed"
						: good.status?.slug === "rejected_incorrect_data" ||
							  good.status?.slug === "rejected_duplicate" ||
							  good.status?.slug === "rejected_not_real_sale" ||
							  good.status?.slug === "rejected_not_enough_information"
							? "rejected"
							: "pending",
			rejectReason: good.status?.slug?.includes("rejected")
				? good.status?.slug === "rejected_incorrect_data"
					? "некорректные данные"
					: good.status?.slug === "rejected_duplicate"
						? "дубликат"
						: good.status?.slug === "rejected_not_real_sale"
							? "не является реальной продажей"
							: good.status?.slug === "rejected_not_enough_information"
								? "недостаточно информации"
								: "отклонено"
				: null,
			originalStatus: good.status,
		}));
		setPositions(transformedPositions);
	};

	// Загрузка данных если не переданы initialData
	useEffect(() => {
		if (!initialData && slug) {
			fetchOrderData();
		} else if (initialData) {
			transformOrderData(initialData);
		}
	}, [slug, initialData]);

	const approveServiceOrder = async (serviceOrderId, goods) => {
		try {
			const response = await apiMethods.myOrdersList.postApproveOrder(
				serviceOrderId,
				goods,
			);
			return response.data;
		} catch (error) {
			console.error("Failed to approve service order:", error);
			throw error;
		}
	};

	const allSelected =
		positions.length > 0 &&
		selectedIds.size === positions.filter(p => p.status === "pending").length;

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(
				new Set(positions.filter(p => p.status === "pending").map(p => p.id)),
			);
		}
	};

	const toggleSelect = id => {
		const newSet = new Set(selectedIds);
		if (newSet.has(id)) {
			newSet.delete(id);
		} else {
			newSet.add(id);
		}
		setSelectedIds(newSet);
	};

	const confirmSelected = async () => {
		if (selectedIds.size === 0) return;

		setLoading(true);
		try {
			const goodsToApprove = Array.from(selectedIds).map(id => ({
				id: id,
				status: "approved",
			}));

			await approveServiceOrder(slug, goodsToApprove);

			// Обновляем позиции
			const updatedPositions = positions.map(pos =>
				selectedIds.has(pos.id) && pos.status === "pending"
					? { ...pos, status: "confirmed", rejectReason: null }
					: pos,
			);
			setPositions(updatedPositions);
			setSelectedIds(new Set());

			// Обновляем основной статус заказа
			updateMainOrderStatus(orderData, updatedPositions);

			if (onStatusChange) {
				onStatusChange();
			}
		} catch (error) {
			console.error("Error confirming positions:", error);
			alert("Ошибка при подтверждении позиций");
		} finally {
			setLoading(false);
		}
	};

	const openMassRejectPopup = () => {
		if (selectedIds.size === 0) return;

		const buttonRect = rejectButtonRef.current?.getBoundingClientRect();
		const containerRect = rejectButtonRef.current
			?.closest(`.${styles.main_blockButtons}`)
			?.getBoundingClientRect();

		if (buttonRect && containerRect) {
			setPopupPosition({
				top: buttonRect.bottom - containerRect.top + 10,
				left: 0,
			});
		}

		setShowPopup("massReject");
		setRejectData({ reason: "invalidData" });
	};

	const openSingleRejectPopup = (id, event) => {
		const buttonRect = event.currentTarget.getBoundingClientRect();
		const popupHeight = 400;
		const popupWidth = 280;
		const isMobile = window.innerWidth <= 768;

		let topPosition = buttonRect.bottom + 10;
		let leftPosition = buttonRect.right - popupWidth;

		if (isMobile) {
			leftPosition = (window.innerWidth - popupWidth) / 2;
			topPosition = buttonRect.bottom - 350;
		} else {
			if (topPosition + popupHeight > window.innerHeight) {
				topPosition = buttonRect.top - popupHeight - 10;
			}

			if (leftPosition + popupWidth > window.innerWidth) {
				leftPosition = buttonRect.left;
			}

			if (leftPosition < 0) {
				leftPosition = 10;
			}
		}

		setSinglePopupPosition({
			top: topPosition,
			left: leftPosition,
		});

		setSelectedPositionId(id);
		setShowPopup("singleReject");
		setRejectData({ reason: "invalidData" });
	};

	const confirmMassReject = async () => {
		if (!rejectData.reason) {
			alert("Выберите причину отказа");
			return;
		}

		let status = "";
		let reasonText = "";

		switch (rejectData.reason) {
			case "invalidData":
				status = "rejected_incorrect_data";
				reasonText = "некорректные данные";
				break;
			case "duplicate":
				status = "rejected_duplicate";
				reasonText = "дубликат";
				break;
			case "notRealSale":
				status = "rejected_not_real_sale";
				reasonText = "не является реальной продажей";
				break;
			case "notEnoughInfo":
				status = "rejected_not_enough_information";
				reasonText = "недостаточно информации";
				break;
			default:
				status = "rejected_incorrect_data";
				reasonText = "некорректные данные";
		}

		setLoading(true);
		try {
			const goodsToReject = Array.from(selectedIds).map(id => ({
				id: id,
				status: status,
			}));

			await approveServiceOrder(slug, goodsToReject);

			const updatedPositions = positions.map(pos =>
				selectedIds.has(pos.id) && pos.status === "pending"
					? { ...pos, status: "rejected", rejectReason: reasonText }
					: pos,
			);
			setPositions(updatedPositions);
			setSelectedIds(new Set());
			setShowPopup(null);
			updateMainOrderStatus(orderData, updatedPositions);

			if (onStatusChange) {
				onStatusChange();
			}
		} catch (error) {
			console.error("Error rejecting positions:", error);
			alert("Ошибка при отклонении позиций");
		} finally {
			setLoading(false);
		}
	};

	const confirmSingleReject = async () => {
		if (!rejectData.reason) {
			alert("Выберите причину отказа");
			return;
		}

		if (!selectedPositionId) return;

		let status = "";
		let reasonText = "";

		switch (rejectData.reason) {
			case "invalidData":
				status = "rejected_incorrect_data";
				reasonText = "некорректные данные";
				break;
			case "duplicate":
				status = "rejected_duplicate";
				reasonText = "дубликат";
				break;
			case "notRealSale":
				status = "rejected_not_real_sale";
				reasonText = "не является реальной продажей";
				break;
			case "notEnoughInfo":
				status = "rejected_not_enough_information";
				reasonText = "недостаточно информации";
				break;
			default:
				status = "rejected_incorrect_data";
				reasonText = "некорректные данные";
		}

		setLoading(true);
		try {
			const goodsToReject = [
				{
					id: selectedPositionId,
					status: status,
				},
			];

			await approveServiceOrder(slug, goodsToReject);

			const updatedPositions = positions.map(pos =>
				pos.id === selectedPositionId && pos.status === "pending"
					? { ...pos, status: "rejected", rejectReason: reasonText }
					: pos,
			);
			setPositions(updatedPositions);
			setShowPopup(null);
			setSelectedPositionId(null);

			if (selectedIds.has(selectedPositionId)) {
				const newSet = new Set(selectedIds);
				newSet.delete(selectedPositionId);
				setSelectedIds(newSet);
			}

			updateMainOrderStatus(orderData, updatedPositions);

			if (onStatusChange) {
				onStatusChange();
			}
		} catch (error) {
			console.error("Error rejecting position:", error);
			alert("Ошибка при отклонении позиции");
		} finally {
			setLoading(false);
		}
	};

	const cancelReject = () => {
		setShowPopup(null);
		setSelectedPositionId(null);
	};

	const confirmSingle = async id => {
		setLoading(true);
		try {
			const goodsToApprove = [
				{
					id: id,
					status: "approved",
				},
			];

			await approveServiceOrder(slug, goodsToApprove);

			// Обновляем позиции
			const updatedPositions = positions.map(pos =>
				pos.id === id && pos.status === "pending"
					? { ...pos, status: "confirmed", rejectReason: null }
					: pos,
			);
			setPositions(updatedPositions);

			if (selectedIds.has(id)) {
				const newSet = new Set(selectedIds);
				newSet.delete(id);
				setSelectedIds(newSet);
			}

			// Обновляем основной статус заказа
			updateMainOrderStatus(orderData, updatedPositions);

			if (onStatusChange) {
				onStatusChange();
			}
		} catch (error) {
			console.error("Error confirming position:", error);
			alert("Ошибка при подтверждении позиции");
		} finally {
			setLoading(false);
		}
	};

	const getOrderStatus = () => {
		if (!positions.length) return "Ожидает подтверждения";

		const hasRejected = positions.some(p => p.status === "rejected");
		const allConfirmed = positions.every(p => p.status === "confirmed");
		const hasConfirmed = positions.some(p => p.status === "confirmed");
		const hasPending = positions.some(p => p.status === "pending");

		if (allConfirmed) {
			return "Подтверждено";
		} else if (hasRejected) {
			return "Отклонено";
		} else if (hasConfirmed && hasPending) {
			return "Частично подтверждено";
		} else {
			return "Ожидает подтверждения";
		}
	};

	// Используем либо обновленный orderData.status, либо вычисляем из positions
	const displayStatus = getOrderStatus();

	const statusIcon =
		displayStatus === "Отклонено"
			? "/images/status_error.svg"
			: displayStatus === "Подтверждено"
				? "/images/status_ok.svg"
				: displayStatus === "Частично подтверждено"
					? "/images/status_wait_2.svg"
					: "";

	if (loading && !positions.length) {
		return <div className={styles.container}>Загрузка...</div>;
	}

	return (
		<section className={styles.container}>
			<div className="">
				<div className={styles.header}>
					<h1>
						Заказ-наряд №{orderData?.number || slug} от{" "}
						{orderData?.created_at
							? new Date(orderData.created_at).toLocaleDateString()
							: ""}
					</h1>
					<div className={styles.main_blockImg + " " + styles.visible_mob}>
						<div>
							<a
								href={orderData?.photos?.[0]?.path}
								className={styles.download}
								download
								target="_blank"
								title="Скачать изображение"
							>
								<img
									className={styles.iconSearch}
									src="/images/download.svg"
									alt=""
								/>
							</a>
							{/* <img
								className={styles.iconSearch}
								src="/images/search.svg"
								alt=""
							/> */}
							<ImageMagnifier
								src={orderData?.photos?.[0]?.path || "/images/test_doc.png"}
								alt="Документ"
								zoomFactor={2.5}
							/>
						</div>
						<div>
							<a target="_blank" href={orderData?.photos?.[0]?.path}>
								Открыть изображение в новой вкладке
							</a>
						</div>
					</div>

					<div className={styles.status_order}>
						{displayStatus}
						{statusIcon !== "" && (
							<img
								src={statusIcon}
								alt={orderData?.status?.slug || "pending"}
							/>
						)}
					</div>
				</div>

				{/* Остальная часть компонента без изменений */}
				<div className={styles.main_block}>
					<div className={styles.main_blockImg + " " + styles.visible_pc}>
						<div>
							<a
								href={orderData?.photos?.[0]?.path}
								className={styles.download}
								download
								target="_blank"
								title="Скачать изображение"
							>
								<img
									className={styles.iconSearch}
									src="/images/download.svg"
									alt=""
								/>
							</a>
							<ImageMagnifier
								src={orderData?.photos?.[0]?.path || "/images/test_doc.png"}
								alt="Документ"
								zoomFactor={2.5}
							/>
						</div>

						<a target="_blank" href={orderData?.photos?.[0]?.path}>
							Открыть изображение в новой вкладке
						</a>
					</div>
					<div className={styles.main_info}>
						<div
							className={styles.main_blockButtons}
							style={{ position: "relative" }}
						>
							<div
								className={"btn btnPrimary btnIcon newBtnUpdate green_b"}
								onClick={confirmSelected}
								style={{
									cursor:
										selectedIds.size === 0 || loading
											? "not-allowed"
											: "pointer",
									opacity: selectedIds.size === 0 || loading ? 0.5 : 1,
								}}
							>
								{loading ? "Обработка..." : "Подтвердить выбранные позиции"}
								<img src="/images/ok_white.svg" alt="" />
							</div>
							<div
								ref={rejectButtonRef}
								className={"btn btnPrimary btnIcon newBtnUpdate"}
								onClick={openMassRejectPopup}
								style={{
									cursor:
										selectedIds.size === 0 || loading
											? "not-allowed"
											: "pointer",
									opacity: selectedIds.size === 0 || loading ? 0.5 : 1,
								}}
							>
								Отклонить выбранные позиции
								<img src="/images/error_white.svg" alt="" />
							</div>

							{/* Попап для массового отказа */}
							{showPopup === "massReject" && selectedIds.size > 0 && (
								<div
									className={styles.popup}
									style={{
										position: "absolute",
										top: popupPosition.top,
										right: 0,
										zIndex: 1000,
									}}
								>
									<p className={styles.title}>Тип ошибки для отказа</p>
									<div className={styles.check_block}>
										<label className={styles.customRadio}>
											<input
												type="radio"
												name="rejectReason"
												checked={rejectData.reason === "invalidData"}
												onChange={() =>
													setRejectData({
														...rejectData,
														reason: "invalidData",
													})
												}
											/>
											<span className={styles.radioMark}></span>
										</label>
										<label
											style={{ marginLeft: "8px", cursor: "pointer" }}
											onClick={() =>
												setRejectData({
													...rejectData,
													reason: "invalidData",
												})
											}
										>
											некорректные данные
										</label>
									</div>
									<div className={styles.check_block}>
										<label className={styles.customRadio}>
											<input
												type="radio"
												name="rejectReason"
												checked={rejectData.reason === "duplicate"}
												onChange={() =>
													setRejectData({
														...rejectData,
														reason: "duplicate",
													})
												}
											/>
											<span className={styles.radioMark}></span>
										</label>
										<label
											style={{ marginLeft: "8px", cursor: "pointer" }}
											onClick={() =>
												setRejectData({
													...rejectData,
													reason: "duplicate",
												})
											}
										>
											дубликат
										</label>
									</div>
									<div className={styles.check_block}>
										<label className={styles.customRadio}>
											<input
												type="radio"
												name="rejectReason"
												checked={rejectData.reason === "notRealSale"}
												onChange={() =>
													setRejectData({
														...rejectData,
														reason: "notRealSale",
													})
												}
											/>
											<span className={styles.radioMark}></span>
										</label>
										<label
											style={{ marginLeft: "8px", cursor: "pointer" }}
											onClick={() =>
												setRejectData({
													...rejectData,
													reason: "notRealSale",
												})
											}
										>
											не является реальной продажей
										</label>
									</div>
									<div className={styles.check_block}>
										<label className={styles.customRadio}>
											<input
												type="radio"
												name="rejectReason"
												checked={rejectData.reason === "notEnoughInfo"}
												onChange={() =>
													setRejectData({
														...rejectData,
														reason: "notEnoughInfo",
													})
												}
											/>
											<span className={styles.radioMark}></span>
										</label>
										<label
											style={{ marginLeft: "8px", cursor: "pointer" }}
											onClick={() =>
												setRejectData({
													...rejectData,
													reason: "notEnoughInfo",
												})
											}
										>
											недостаточно информации
										</label>
									</div>
									<p className={styles.bottom_p}>
										<b>Подтверждаете ли свой выбор:</b>
										<br />{" "}
										{rejectData.reason === "invalidData"
											? "некорректные данные"
											: rejectData.reason === "duplicate"
												? "дубликат"
												: rejectData.reason === "notRealSale"
													? "не является реальной продажей"
													: rejectData.reason === "notEnoughInfo"
														? "недостаточно информации"
														: ""}
									</p>
									<div style={{ display: "flex", gap: "10px" }}>
										<div
											className={
												"btn btnPrimary newBtnUpdate " + styles.popupYesNo
											}
											onClick={confirmMassReject}
										>
											Да
										</div>
										<div
											className={
												"btn btnPrimary newBtnUpdate " + styles.popupYesNo
											}
											onClick={cancelReject}
										>
											Нет
										</div>
									</div>
								</div>
							)}
						</div>
						<div>
							<div className={styles.table_div + " scrollbox"}>
								<table className="statTable">
									<thead>
										<tr>
											<th>
												<label className={styles.customCheckbox}>
													<input
														type="checkbox"
														checked={allSelected}
														onChange={toggleSelectAll}
														disabled={loading}
													/>
													<span className={styles.checkmark}></span>
												</label>
											</th>
											<th style={{ minWidth: "130px" }}>
												<div>Масло</div>
											</th>
											<th>
												<div>Тип тары</div>
											</th>
											<th>
												<div>Кол-во литров</div>
											</th>
											<th>
												<div>Кол-во, шт.</div>
											</th>
											<th>Сумма продажи масла, руб.</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{positions.map(pos => (
											<tr key={pos.id}>
												<td>
													<label className={styles.customCheckbox}>
														<input
															type="checkbox"
															checked={selectedIds.has(pos.id)}
															onChange={() => toggleSelect(pos.id)}
															disabled={pos.status !== "pending" || loading}
														/>
														<span className={styles.checkmark}></span>
													</label>
												</td>
												<td>{pos.oil}</td>
												<td>{pos.container}</td>
												<td>{pos.liters}</td>
												<td>{pos.quantity}</td>
												<td>{pos.price.toLocaleString()}</td>
												<td className={styles.buttons_active_td}>
													{pos.status === "pending" ? (
														<div style={{ position: "relative" }}>
															<div className={styles.main_blockButtons}>
																<div
																	className={
																		"btn btnPrimary btnIcon newBtnUpdate green_b"
																	}
																	onClick={() => confirmSingle(pos.id)}
																	style={{ opacity: loading ? 0.5 : 1 }}
																>
																	Подтвердить{" "}
																	<img src="/images/ok_white.svg" alt="" />
																</div>
																<div
																	className={
																		"btn btnPrimary btnIcon newBtnUpdate"
																	}
																	onClick={e =>
																		openSingleRejectPopup(pos.id, e)
																	}
																	style={{ opacity: loading ? 0.5 : 1 }}
																>
																	Отклонить{" "}
																	<img src="/images/error_white.svg" alt="" />
																</div>
															</div>
														</div>
													) : pos.status === "confirmed" ? (
														<div className={styles.status}>
															<div>
																<span
																	className={`${styles.stateValue} ${styles.statusConfirmed}`}
																>
																	Подтверждено
																</span>
																{/* <img
																	src="/images/status_ok.svg"
																	alt="Подтверждено"
																/> */}
															</div>
														</div>
													) : (
														<div className={styles.status}>
															<div>
																<span
																	className={`${styles.stateValue} ${styles.statusConfirmed}`}
																>
																	Отклонено, {pos.rejectReason}
																</span>
																{/* <img
																	src="/images/status_error.svg"
																	alt="отклонено"
																/> */}
															</div>
														</div>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
						<div className={styles.mb_main}>
							<div
								className={styles.title_table}
								onClick={toggleTable}
								style={{ cursor: "pointer" }}
							>
								Товары, участвующие в акции:{" "}
								<span
									style={{
										display: "inline-block",
										transform: isTableVisible
											? "rotate(0deg)"
											: "rotate(180deg)",
										transition: "transform 0.3s ease-out",
									}}
								>
									<img src="/images/arrow.svg" alt="" />
								</span>
							</div>
							<div
								className={styles.table_custom_css}
								style={{
									maxHeight: isTableVisible ? "1000px" : "0",
									overflow: "hidden",
									transition: "max-height 0.3s ease-out",
								}}
							>
								<Table
									overflow="auto"
									headers={[
										{ field: "name", title: "Наименование*" },
										{ field: "volume", title: "Единица измерения" },
										{ field: "price", title: "Количество баллов**" },
									]}
									tableData={[
										{ name: "ZIC TOP", volume: "1 литр", price: 25 },
										{ name: "ZIC ZERO", volume: "1 литр", price: 25 },
										{ name: "ZIC X9", volume: "1 литр", price: 20 },
										{ name: "ZIC X7", volume: "1 литр", price: 15 },
										{ name: "ZIC X8", volume: "1 литр", price: 20 },
										{ name: "ZIC ULTRA", volume: "1 литр", price: 25 },
										{ name: "ZIC X9000", volume: "1 литр", price: 25 },
										{ name: "ZIC X8000", volume: "1 литр", price: 20 },
										{ name: "ZIC X7000", volume: "1 литр", price: 15 },
									]}
									isLoading={false}
								/>
								<p className={styles.ball_title}>1 балл = 1 рубль</p>
								<p style={{ color: "rgba(0, 0, 0, 0.5)" }}>
									* Наименование продукции может быть изменено
									<br />
									** Количество начисляемых баллов может быть изменено
									<br />В акции участвуют заказ-наряды с датой продажи не ранее
									01 апреля 2026 г.
								</p>{" "}
								<Link
									style={{ color: "rgba(0, 0, 0, 0.5)" }}
									to="https://storage.yandexcloud.net/zictop.public/documents/rules.pdf"
									target="_blank"
								>
									Полные правила
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Единый попап для подтверждения/отклонения вне таблицы */}
			{showPopup === "singleReject" && selectedPositionId && (
				<div
					className={styles.popup}
					ref={singlePopupRef}
					style={{
						position: "fixed",
						top: `${singlePopupPosition.top}px`,
						left: `${singlePopupPosition.left}px`,
						zIndex: 9999,
					}}
				>
					<p className={styles.title}>Тип ошибки для отказа</p>
					<div className={styles.check_block}>
						<label className={styles.customRadio}>
							<input
								type="radio"
								name="rejectReason"
								checked={rejectData.reason === "invalidData"}
								onChange={() =>
									setRejectData({
										...rejectData,
										reason: "invalidData",
									})
								}
							/>
							<span className={styles.radioMark}></span>
						</label>
						<label
							style={{ marginLeft: "8px", cursor: "pointer" }}
							onClick={() =>
								setRejectData({
									...rejectData,
									reason: "invalidData",
								})
							}
						>
							некорректные данные
						</label>
					</div>
					<div className={styles.check_block}>
						<label className={styles.customRadio}>
							<input
								type="radio"
								name="rejectReason"
								checked={rejectData.reason === "duplicate"}
								onChange={() =>
									setRejectData({
										...rejectData,
										reason: "duplicate",
									})
								}
							/>
							<span className={styles.radioMark}></span>
						</label>
						<label
							style={{ marginLeft: "8px", cursor: "pointer" }}
							onClick={() =>
								setRejectData({
									...rejectData,
									reason: "duplicate",
								})
							}
						>
							дубликат
						</label>
					</div>
					<div className={styles.check_block}>
						<label className={styles.customRadio}>
							<input
								type="radio"
								name="rejectReason"
								checked={rejectData.reason === "notRealSale"}
								onChange={() =>
									setRejectData({
										...rejectData,
										reason: "notRealSale",
									})
								}
							/>
							<span className={styles.radioMark}></span>
						</label>
						<label
							style={{ marginLeft: "8px", cursor: "pointer" }}
							onClick={() =>
								setRejectData({
									...rejectData,
									reason: "notRealSale",
								})
							}
						>
							не является реальной продажей
						</label>
					</div>
					<div className={styles.check_block}>
						<label className={styles.customRadio}>
							<input
								type="radio"
								name="rejectReason"
								checked={rejectData.reason === "notEnoughInfo"}
								onChange={() =>
									setRejectData({
										...rejectData,
										reason: "notEnoughInfo",
									})
								}
							/>
							<span className={styles.radioMark}></span>
						</label>
						<label
							style={{ marginLeft: "8px", cursor: "pointer" }}
							onClick={() =>
								setRejectData({
									...rejectData,
									reason: "notEnoughInfo",
								})
							}
						>
							недостаточно информации
						</label>
					</div>
					<p className={styles.bottom_p}>
						<b>Подтверждаете ли свой выбор:</b>
						<br />{" "}
						{rejectData.reason === "invalidData"
							? "некорректные данные"
							: rejectData.reason === "duplicate"
								? "дубликат"
								: rejectData.reason === "notRealSale"
									? "не является реальной продажей"
									: rejectData.reason === "notEnoughInfo"
										? "недостаточно информации"
										: ""}
					</p>
					<div style={{ display: "flex", gap: "10px" }}>
						<div
							className={"btn btnPrimary newBtnUpdate " + styles.popupYesNo}
							onClick={() => confirmSingleReject(selectedPositionId)}
						>
							Да
						</div>
						<div
							className={"btn btnPrimary newBtnUpdate " + styles.popupYesNo}
							onClick={cancelReject}
						>
							Нет
						</div>
					</div>
				</div>
			)}
		</section>
	);
}
